import { noop } from '@tanstack/react-query';
import { isObject, merge } from 'lodash-es';
import { RhinoContextType } from '../RhinoContext';
import {
  RhinoFilter,
  RhinoOrder,
  RhinoResource,
  RhinoResourceSpecifier
} from '..';
import { modelKey } from './queries';
import { DEFAULT_LIMIT, defaultOrderTerms } from './controllers';
import { getBaseOwnerFilters } from '../utils/models';
import { networkApiCallOnlyData } from '../lib/networking';

export type PrefetchModelIndexOptions<
  T extends RhinoResourceSpecifier = RhinoResourceSpecifier
> = {
  defaultFilter?: RhinoFilter<T>;
  defaultFiltersBaseOwner?: boolean;
  defaultLimit?: number;
  defaultOrder?: RhinoOrder<T> | null;
};

/**
 * Prefetch a model index query from a route loader so data is ready when the
 * component mounts. The query key matches what useModelIndexController /
 * useModelIndex produce for the same defaults, so React Query deduplicates.
 *
 * Return the promise from the loader so TanStack Router can show the pending
 * component while the data loads.
 *
 * @example
 *   // Simple — prefetch with default limit/order
 *   loader: ({ context: { rhino } }) => {
 *     return prefetchModelIndex(rhino, 'blog');
 *   }
 *
 * @example
 *   // With custom defaults matching the page's ModelIndexSimple props
 *   loader: ({ context: { rhino }, params: { blog_id } }) => {
 *     return prefetchModelIndex(rhino, 'blog_post', {
 *       defaultFilter: { blog_id, category: { kind: 'published' } },
 *       defaultLimit: 10000,
 *       defaultOrder: '-published_at,blog.title,title',
 *     });
 *   }
 */
/**
 * Prefetch a model show query from a route loader so data is ready when the
 * component mounts. The query key matches what useModelKeyShow / useModelShow
 * produce, so React Query deduplicates.
 *
 * Return the promise from the loader so TanStack Router can show the pending
 * component while the data loads.
 *
 * @example
 *   loader: ({ context: { rhino }, params: { id } }) => {
 *     return prefetchModelShow(rhino, 'blog_post', id);
 *   }
 */
export const prefetchModelShow = (
  rhino: RhinoContextType,
  model: RhinoResourceSpecifier,
  id: string | number
) => {
  const resolvedModel: RhinoResource = isObject(model)
    ? model
    : rhino.resources[model];

  return rhino.queryClient
    .query({
      queryKey: [modelKey(resolvedModel, 'show'), `${id}`],
      queryFn: ({ signal }) =>
        networkApiCallOnlyData(
          resolvedModel.singular
            ? resolvedModel.path
            : `${resolvedModel.path}/${id}`,
          { signal }
        )
    })
    .then(noop, noop);
};

export const prefetchModelIndex = <T extends RhinoResourceSpecifier>(
  rhino: RhinoContextType,
  model: T,
  options?: PrefetchModelIndexOptions<T>
) => {
  const {
    defaultFilter,
    defaultFiltersBaseOwner = true,
    defaultLimit,
    defaultOrder
  } = options ?? {};

  // Narrowing reads the union, not the parameter bounded by it.
  const specifier: RhinoResourceSpecifier = model;
  const resolvedModel: RhinoResource = isObject(specifier)
    ? specifier
    : rhino.resources[specifier];
  const baseOwnerId = rhino.baseOwner?.id;

  const filter = defaultFiltersBaseOwner
    ? (merge(getBaseOwnerFilters(resolvedModel, baseOwnerId), defaultFilter) ??
      {})
    : (defaultFilter ?? {});

  const queryOptions = {
    filter,
    geospatial: {},
    limit: defaultLimit ?? DEFAULT_LIMIT,
    offset: 0,
    order: defaultOrderTerms(defaultOrder),
    search: ''
  };

  return rhino.queryClient
    .query({
      queryKey: [modelKey(resolvedModel, 'index'), queryOptions],
      queryFn: ({ signal }) =>
        networkApiCallOnlyData(resolvedModel.path, {
          params: queryOptions,
          signal
        })
    })
    .then(noop, noop);
};
